// The block-level halves of SuiteEditor's ProseCommands (`EditingRules.block`, `restyle`, `aligned`, `listed`…): pure
// functions over a fragment's text, used by the format commands.
import type { Alignment, ListStyle } from '@writing-suite/core';
import { escapeLeading, marker, paragraphFence, verseFence } from '@writing-suite/core';
import { parseListMarker, scan, trimmed } from './markdown.ts';

/** The block styles the dialect stores (`ProseBlockStyle`): what the format bar's style menu offers. */
export type BlockStyle = 'body' | 'blockQuote' | 'epigraph' | 'verse';

const blank = (s: string) => s.trim().length === 0;

/** `NSString.lineRange(for:)` at `i`: the line holding `i`, with its newline. */
function lineRange(t: string, i: number): [number, number] {
  const start = i <= 0 ? 0 : t.lastIndexOf('\n', i - 1) + 1;
  const nl = t.indexOf('\n', i);
  return [start, nl < 0 ? t.length : nl + 1];
}

/** The block around `location` in a body: its run of non-blank lines, without the newline after it
 *  (`EditingRules.block`). Empty (at `location`) on a blank line. A fence is a block of its own, from its opening line
 *  to its `:::`, even with no blank line before or after it, so a paragraph typed under a poem is never restyled with
 *  the poem. */
export function block(body: string, location: number): [number, number] {
  const at = Math.min(Math.max(0, location), body.length);
  const text = (r: [number, number]) => trimmed(body.slice(r[0], r[1]).replace(/\n$/, ''));
  const isBlank = (r: [number, number]) => text(r).length === 0;
  const opens = (r: [number, number]) => { const t = text(r); return t === '::: epigraph' || paragraphFence(t) !== null; };
  const closes = (r: [number, number]) => text(r) === ':::';
  const current = lineRange(body, at);
  if (isBlank(current)) return [at, at];
  let start = current[0], end = current[1];
  if (!opens(current)) {
    while (start > 0) {
      const previous = lineRange(body, start - 1);
      if (isBlank(previous) || closes(previous)) break;
      start = previous[0];
      if (opens(previous)) break;
    }
  }
  if (!closes(current)) {
    while (end < body.length) {
      const next = lineRange(body, end);
      if (isBlank(next) || opens(next)) break;
      end = next[1];
      if (closes(next)) break;
    }
  }
  while (end > start && body.charCodeAt(end - 1) === 10) end--;
  return [start, end];
}

/** Whether `location` in a body is on a line inside a fence: below a `::: …` opening line with no `:::` closing it
 *  before that line (`EditingRules.isInsideFence`). */
export function isInsideFence(body: string, location: number): boolean {
  const here = lineRange(body, Math.min(Math.max(0, location), body.length))[0];
  let open = false;
  for (let i = 0; i < here;) {
    const line = lineRange(body, i);
    const t = trimmed(body.slice(line[0], line[1]).replace(/\n$/, ''));
    open = open ? t !== ':::' : t === '::: epigraph' || paragraphFence(t) !== null;
    i = line[1];
  }
  return open;
}

const lines = (s: string) => s.split('\n');

/** Which style a block is written in (an aligned paragraph or a list is body text). */
export function blockStyle(b: string): BlockStyle {
  const ls = lines(b);
  if (ls[0].startsWith(':::')) {
    const t = trimmed(ls[0]);
    if (t === '::: epigraph') return 'epigraph';
    return paragraphFence(t)?.kind.type === 'verse' ? 'verse' : 'body';
  }
  if (ls.every((l) => l.startsWith('>'))) return 'blockQuote';
  return 'body';
}

/** A paragraph's alignment, when it's set in a fence. */
export function alignment(b: string): Alignment | null {
  const first = lines(b).find((l) => l.length > 0);
  if (first === undefined) return null;
  return paragraphFence(trimmed(first))?.alignment ?? null;
}

/** The style of the list a block is, if it's one. */
export function listStyle(b: string): ListStyle | null {
  const first = lines(b).find((l) => l.length > 0);
  return first === undefined ? null : parseListMarker(first)?.style ?? null;
}

/** A block's text without its block markup: one entry per list item, else one for the whole block. */
export function contents(b: string): string[] {
  let ls = lines(b);
  if (ls[0]?.startsWith(':::')) {
    ls = ls.slice(1);
    if (ls.length && trimmed(ls[ls.length - 1]) === ':::') ls = ls.slice(0, -1);
    return [ls.join('\n')];
  }
  if (ls.length && ls.every((l) => l.startsWith('>'))) {
    return [ls.map((l) => { let s = l.slice(1); if (s.startsWith(' ')) s = s.slice(1); return s; }).join('\n')];
  }
  if (listStyle(b) === null) return [b];
  const spans = scan(b);
  const items: string[] = [];
  for (const s of spans) {
    if (s.kind !== 'listItem') continue;
    const line = b.slice(s.from, s.to);
    const m = spans.find((x) => x.kind === 'listMarker' && x.from === s.from);
    if (m) items.push(line.slice(m.to - m.from));
    else if (items.length) items[items.length - 1] += '\n' + line;
  }
  return items;
}

/** The block rewritten in another style: its words kept, its markup changed (`EditingRules.restyle`). */
export function restyle(b: string, style: BlockStyle): string {
  const items = contents(b);
  if (style === 'body') return items.join('\n\n');
  const ls = items.join('\\\n').split('\n');
  if (style === 'blockQuote') return ls.map((l) => '> ' + l).join('\n');
  // A stanza: the block's lines (a list's items a line each), keeping a centred block centred.
  if (style === 'verse') return [verseFence(alignment(b)), ...ls, ':::'].join('\n');
  return ['::: epigraph', ...ls, ':::'].join('\n');
}

export function isSceneBreakOrNote(b: string): boolean {
  const t = b.trim();
  return t === '***' || t === '* * *' || (t.startsWith('[^') && t.includes(']:'));
}

/** Body text: not a list, quotation, epigraph, scene break or note. */
export function isParagraph(b: string): boolean {
  return blockStyle(b) === 'body' && listStyle(b) === null && !isSceneBreakOrNote(b);
}

/** Body paragraphs among `blocks` set to `a` (null: flush left); other blocks are left (`EditingRules.aligned`). */
export function aligned(blocks: string[], a: Alignment | null): string[] {
  return blocks.map((b) => {
    if (!isParagraph(b)) {
      // Verse carries its alignment in its own fence line (never justified: left is its own).
      if (blockStyle(b) !== 'verse') return b;
      const ls = b.split('\n');
      ls[0] = verseFence(a === 'justify' ? null : a);
      return ls.join('\n');
    }
    const text = contents(b)[0];
    return a ? `::: ${a}\n${text}\n:::` : text;
  });
}

/** `blocks` made into lists of `style` (each run of paragraphs, quotations and lists becomes one list, numbered from
 *  its start), or with null their lists made paragraphs again (`EditingRules.listed`). */
export function listed(blocks: string[], style: ListStyle | null): string[] {
  const out: string[] = [];
  let items: string[] = [];
  const flush = () => {
    if (items.length === 0 || !style) return;
    out.push(items.map((item, n) => {
      const ls = item.split('\n');
      return [marker(style, n + 1) + '\t' + ls[0], ...ls.slice(1).map((l) => escapeLeading(l))].join('\n');
    }).join('\n'));
    items = [];
  };
  for (const b of blocks) {
    const listable = isParagraph(b) || listStyle(b) !== null || blockStyle(b) !== 'body';
    if (!listable || isSceneBreakOrNote(b)) { flush(); out.push(b); continue; }
    if (style === null) out.push(...(listStyle(b) === null ? [b] : contents(b)));
    else items.push(...contents(b));
  }
  flush();
  return out;
}

/** How much markup opens a block's first line: a fence's whole line, a quotation's `> `, a list item's marker. */
export function leadLength(b: string): number {
  const first = b.split('\n', 1)[0] ?? '';
  if (first.startsWith(':::')) return first.length + 1;
  if (first.startsWith('>')) return first.startsWith('> ') ? 2 : 1;
  const m = parseListMarker(first);
  return m ? m.length : 0;
}

const segmenter = typeof Intl !== 'undefined' && 'Segmenter' in Intl ? new Intl.Segmenter(undefined, { granularity: 'word' }) : null;

/** The word around `location` (a caret), for styling it when nothing is selected (`EditingRules.word`). */
export function word(t: string, location: number): [number, number] | null {
  const [ls, le] = lineRange(t, Math.min(location, t.length));
  const line = t.slice(ls, le);
  if (segmenter) {
    for (const seg of segmenter.segment(line)) {
      if (!seg.isWordLike) continue;
      const a = ls + seg.index, b = a + seg.segment.length;
      if (a > location) break;
      if (a <= location && location <= b) return [a, b];
    }
    return null;
  }
  const re = /[\p{L}\p{N}’']+/gu;
  for (let m = re.exec(line); m; m = re.exec(line)) {
    const a = ls + m.index, b = a + m[0].length;
    if (a > location) break;
    if (location <= b) return [a, b];
  }
  return null;
}

/** The next footnote number in a body: one past the highest `[^n]` used. */
export function nextFootnoteNumber(body: string): number {
  let max = 0;
  for (const m of body.matchAll(/\[\^(\d+)\]/g)) max = Math.max(max, parseInt(m[1], 10));
  return max + 1;
}
